import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LightShowType } from '../_models/LightShowType';
import { CUEServiceBase, LiveServiceFile, LiveServiceFileCollection, LiveServiceFormDataBase } from './cue-base.service';
import { CUEServiceListViewBase } from '../_shared/base.management.component';
import { Subject, Observable } from 'rxjs';
import { JWTTokenService } from './jwtToken.service';
import { User } from '../_models/User';

@Injectable()
export class LightShowService extends CUEServiceBase<LightShowFormData, LightShowListView>  {
  private _selectedOperations: string[] = [];
  private _isCtrlPressed: boolean;
  private _isShiftPressed: boolean;
  /**
  * Subject which tells lightshow-preview to update lightShowJson when an operation is changed in any way
  */
  operationArrayChange$: Subject<void> = new Subject();
  selectedOperationChange$: Subject<void> = new Subject();
  torchFreezer$: Subject<boolean> = new Subject();
  operationSelected$: Subject<string> = new Subject();
  operationDeselected$: Subject<string> = new Subject();
  deleteKeyPressed$: Subject<any> = new Subject();
  controlPlusAPressed$: Subject<any> = new Subject();
  sortSegments: Subject<void> = new Subject();
  trackIdManualChange$: Subject<void> = new Subject();

  MINIMUM_TORCH_DURATION = 200;

  userToken: any;
  decodedToken: any;

  openFullScreen$: Subject<void> = new Subject();
  showFullScreenPreview = false;

  constructor(
    http: HttpClient, jwtTokenService: JWTTokenService) {
    super(http, 'light-show', jwtTokenService);
  }

  get selectedOperations() {
    return this._selectedOperations;
  }

  set selectedOperations(val: string[]) {
    this._selectedOperations = val;
  }

  selectOperation(uniqueId: string) {

    if (!this.isCtrlPressed && !this.isShiftPressed) {
      this.selectedOperations = [];

      if (uniqueId) {
        this.selectedOperations.push(uniqueId);
      }
    } else if (this.isShiftPressed) {
      if (!this.selectedOperations.includes(uniqueId)) {
        this.selectedOperations.push(uniqueId);
      }
    } else
      if (uniqueId && !this.selectedOperations.includes(uniqueId)) {
        this.selectedOperations.push(uniqueId);
    } else if (uniqueId) {
      this.selectedOperations.splice(this.selectedOperations.indexOf(uniqueId), 1);
    }

    this.selectedOperationChange$.next();
  }

  deselectOperation(uniqueId: string) {
    const index = this.selectedOperations.indexOf(uniqueId);
    if (index > -1) {
      this.selectedOperations.splice(index, 1);
    }
    this.selectedOperationChange$.next();
  }

  public checkIfOperationIsSelected(uniqueId: string) {
    return this.selectedOperations.includes(uniqueId);
  }

  public setIsOpen (id: number, isOpen: boolean) {

    return this.HttpPost(`light-show/set-is-open/${id}`, isOpen);
  }

  public getIsOpen (id: number) : Observable<any> {

    return this.HttpGet(`light-show/get-is-open/${id}`);
  }

  get isCtrlPressed() {
    return this._isCtrlPressed;
  }

  set isCtrlPressed(val: boolean) {
    this._isCtrlPressed = val;
  }

  set isShiftPressed(val: boolean) {
    this._isShiftPressed = val;
  }

  get isShiftPressed() {
    return this._isShiftPressed;
  }

  public getServiceDisplayName(): string {
    return 'Light Show';
  }

  public getLightShowTypes() {
    return this.CachedHttpGet<LightShowType[]>('light-show/types');
  }

  public saveCurrentLightShow(formData: LightShowFormData): Observable<any> {
    return this.HttpPost('light-show/saved-light-show', formData);
  }

  public copy(id: number): Observable<LightShowListView> {
    this.ClearCache();
    return this.HttpGet('light-show/copy/' + id);
  }

  public getSavedLightShow(): Observable<SavedLightShow> {
    return this.HttpGet('light-show/saved-light-show');
  }

  public deleteSavedLightShow(): Observable<boolean> {
    return this.HttpDelete('light-show/saved-light-show');
  }

  public getShareableLightShow(guid: string): Observable<LightShowFormData> {

    return this.HttpGet(`light-show/public-preview/${guid}`);
  }

  public throttled(delay: number, fn: any) {
    let lastCall = 0;
    return function (...args) {
      const now = (new Date).getTime();
      if (now - lastCall < delay) {
        return;
      }
      lastCall = now;
      return fn(...args);
    };
  }

}

export interface SavedLightShow {
  savedLightShowId: number;
  lightShowFormJSON: string;
  userId: number;
  dateCreated: string;
}

export interface LightShowListView extends CUEServiceListViewBase {
  typeCode: string;
  trackName: number;
  videoName: number;
  isPendingDesign: boolean;
  hasSelfieCam: boolean;
  createdBy: string;
  hasCoupon: boolean;
  status: number;
  redirectType: LightShowRedirectType;
  triggerFileDownloadUrl: string;
}

export class LightShowFormData implements LiveServiceFormDataBase {
    name: string;
    typeCode: string;
    startDate: string;
    endDate: string;
    trackId: number;
    assetData: string;
    videoId: number;
    json: string;
    couponId: number;
    redirectUrl: string;
    alertTitle: string;
    alertBody: string;
    alertButtonTitle: string;
    hasSelfieCam: boolean;
    useAudible: boolean;
    regenerateAudio: boolean;
    insertionPattern: string;
    venueId: number;
    delayDistance: number;
    numberOfWinners: number;
    winnerMessage: string;
    listOfEmails: string[];
    fileDownloadUrl: string;
    selfieCamId: number;
    redirectType: LightShowRedirectType;
    dateCreated: Date;
    serviceFiles: LightShowServiceFileCollection;
    user: User
}

export class LightShowServiceFileCollection implements LiveServiceFileCollection {

  files: LightShowServiceFile[];

  constructor() {

    this.files = [];
  }

  public addImage(file: File) {

    if (file) {

      this.files?.push({ file, fileType: LightShowFileType.Image });
    }
  }

  public addTrack(file: File) {

    if (file) {

      const trackFileIndex = this.files?.findIndex(f => f.fileType === LightShowFileType.Track);

      if (trackFileIndex > -1) {

        this.files[trackFileIndex].file = file;

      } else {

        this.files?.push({ file, fileType: LightShowFileType.Track});

      }
    }
  }
}

export class LightShowServiceFile implements LiveServiceFile {
  file: File;
  fileType: LightShowFileType;
}

export enum LightShowFileType {
  Image,
  Track,
  Audio
}

export enum LightShowRedirectType {
  None = 0,
  Coupon = 1,
  Url = 2,
  Upn = 3,
  Reward = 4,
  Sms = 5,
  SelfieCam = 6
}
